import preventScrolling from "../../../../client-utils/scroll/preventScrolling.util.js";
import enableScrolling from "../../../../client-utils/scroll/enableScrolling.util.js";
import { isAppTypeUtil as isAppType } from "../FeatureDiscovery.utils.js";
import isUndefined from "../../../../common-utils/undefined/isUndefined.util.js";
import { convertApplication } from "../../../../feature-discovery/utils/convertApplications.util.js";
// import hooks
import { useState, useEffect, useLayoutEffect } from "react";
import { useCalculatePosition } from "./useCalculatePosition.js";
import useGetSDBProductTourUserDataQuery from "../../../../feature-discovery/generated/GetSDBProductTourUserDataQuery/get.hook.js";
import usePostSDBProductTourUserData from "../../../../feature-discovery/generated/PostSDBProductTourUserData/post.hook.js";
import { useEnvironmentContext } from "../../../../components/providers/Environment/index.js";
import { SINGLE } from "../../../../constants/numbers.consts.js";
import { TOOLTIP_PAPER_WIDTH, TOOLTIP_PAPER_HEIGHT, TOOLTIP_OFFSET } from "../constants.js";
export const useFeatureDiscovery = ({ steps, featureDiscoveryName, onFinish, tourVersion, viewed, setViewed, }) => {
    /** STATE */
    const { appName, userId, tenantId } = useEnvironmentContext();
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = steps?.length;
    const offset = steps[currentStep]?.offset;
    const { elementPosition, tooltipPosition, updatePosition } = useCalculatePosition({
        paperWidth: TOOLTIP_PAPER_WIDTH,
        paperHeight: TOOLTIP_PAPER_HEIGHT,
        offset: TOOLTIP_OFFSET,
        elementSelector: () => {
            const element = steps[currentStep]?.selector();
            return element instanceof HTMLElement ? element : null;
        },
    });
    /** FETCH DATA */
    const { trigger } = usePostSDBProductTourUserData();
    const convertedAppName = convertApplication(appName);
    const { data, isLoading } = useGetSDBProductTourUserDataQuery({
        application: convertedAppName,
        id: userId ?? "",
        tenant: tenantId ?? "",
    }, undefined, {
        shouldFetch: isUndefined(viewed) && isAppType(convertedAppName) && !!userId?.trim() && !!tenantId?.trim(),
    });
    useLayoutEffect(() => {
        if (data?.data.find(({ productTourId }) => productTourId === featureDiscoveryName) && isUndefined(viewed)) {
            setViewed();
        }
    }, [data?.data, viewed]);
    /** HANDLERS */
    const onFinishHandler = () => {
        if (isAppType(convertedAppName) && userId?.trim() && tenantId?.trim() && isUndefined(viewed)) {
            trigger({
                body: {
                    userId,
                    tenant: tenantId,
                    application: convertedAppName,
                    productTourVersion: tourVersion,
                    productTourId: featureDiscoveryName,
                    productTourCompleted: true,
                },
            })
                .then(() => {
                console.log("successfully posted completion of the tour");
            })
                .catch((e) => {
                console.error("It went wrong while posting the completion of the tour");
                console.error(e);
            })
                .finally(() => {
                setViewed();
                onFinish();
            });
        }
        else {
            setViewed();
            onFinish();
        }
    };
    // Close on end feature discovery
    const closeFeatureDiscoveryHandler = () => {
        steps[currentStep]?.onExit?.().catch(console.error);
        setCurrentStep(steps.length);
        onFinishHandler();
    };
    // Goto next step in feature discovery
    const goToNextStep = async () => {
        await steps[currentStep]?.onNext?.();
        setCurrentStep(currentStep + SINGLE);
        if (currentStep >= steps.length - SINGLE) {
            onFinishHandler();
        }
    };
    // Goto previous step in feature discovery
    const goToPreviousStep = async () => {
        if (currentStep > 0) {
            await steps[currentStep]?.onBack?.();
            setCurrentStep(currentStep - SINGLE);
        }
    };
    // Calculate the position and scrolling
    useEffect(() => {
        if (isLoading || viewed) {
            enableScrolling();
            return;
        }
        void updatePosition(); // Initial position calculation
        preventScrolling(); // Prevent scrolling
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeFeatureDiscoveryHandler();
            }
        };
        // Recalculate position on window resize
        const handleResize = () => {
            void updatePosition();
        };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleResize);
            enableScrolling();
        };
    }, [updatePosition, closeFeatureDiscoveryHandler, isLoading, viewed]);
    return {
        totalSteps,
        offset,
        currentStep,
        viewed,
        isLoading,
        elementPosition,
        tooltipPosition,
        closeFeatureDiscoveryHandler,
        goToNextStep,
        goToPreviousStep,
    };
};
export default useFeatureDiscovery;
